import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { LoadingSkeleton, Selector, showNotification, useQueryFetch } from 'sonata-react-components';
import { normalizeCountryToCca2 } from 'sonata-static-storage/lib/helpers/countries.helpers';

import { useMultiFlightMap } from '../../MultiFlightMap.context';
import { getLocations, getScreensInfo, getStreets } from '../../multiFlightMap.resources';
import { type DropdownSuggestion, SearchDropdown } from '../common/SearchDropdown/SearchDropdown';
import { TopBarStyled } from './TopBar.style';

import type { ReactNode } from 'react';
import type { SelectorOptionPropsInterface } from 'sonata-react-components';
import type { Shape } from 'sonata-static-storage/lib/common/common.types';
import type { GeoDataAndScreensInteface } from 'sonata-static-storage/lib/schemas/screen/screen.schema';
import type { Streets } from '../../multiFlightMap.types';

type SuggestionType = 'locations' | 'screens' | 'streets';

type Suggestions =
  | ({
      suggestion: {
        id: string;
        subtitle: string;
        title: string;
      };
      type: 'locations';
    } & Shape)[]
  | ({
      suggestion: {
        id: string;
        subtitle: string;
        title: string;
      };
      type: 'streets';
    } & Streets)[]
  | ({
      suggestion: {
        id: string;
        subtitle: string;
        title: string;
      };
      type: 'screens';
    } & GeoDataAndScreensInteface)[];

type State = {
  lastSearchedTerm: string;
  search: string;
  suggestionType: SuggestionType;
};

type Props = {
  onTemporaryPositionChange: (latitude: number, longitude: number) => void;
  volumetryAndForecast?: ReactNode;
};

const SUGGESTION_COUNT = 2;

export function TopBar({ onTemporaryPositionChange, volumetryAndForecast }: Props) {
  const { formatMessage } = useIntl();
  const {
    addTemporaryCartographicLocation,
    countries,
    fullShapes,
    loadingLocationList,
    loadingScreensList,
    locations,
    setTemporaryScreen,
    temporaryScreens,
    temporaryVenues,
    type
  } = useMultiFlightMap();
  const [{ lastSearchedTerm, search, suggestionType }, setState] = useState<State>({
    lastSearchedTerm: '',
    search: '',
    suggestionType: type === 'dooh' ? 'screens' : 'locations'
  });
  const isSearchingRef = useRef(false);
  const country = countries[0];
  const isListLoading = type === 'dooh' ? loadingScreensList : loadingLocationList;

  const excludeScreens = temporaryScreens.reduce<string[]>((acc, s) => {
    if (s.device_id && !s.deleted) {
      acc.push(s.device_id);
    }
    return acc;
  }, []);

  const suggestionTypeMethodsMap: Record<
    SuggestionType,
    {
      method: (country: string, query: string) => Promise<Suggestions>;
      subtitleProperty: string;
      titleProperty: string;
    }
  > = {
    locations: {
      method: async (countryParam, queryParam) => {
        const locationIds = locations.map(location => location.cartographyLevelId as string);
        const locationSuggestions = await getLocations(countryParam, queryParam, locationIds);

        if (!locationSuggestions || !locationSuggestions.items || locationSuggestions.items.length === 0) {
          showNotification(
            'map',
            formatMessage({
              defaultMessage: 'No results found',
              id: 'common.noResults'
            })
          );
          return [];
        }

        return locationSuggestions.items.map(s => ({
          ...s,
          suggestion: {
            id: s._id,
            subtitle: formatMessage({
              defaultMessage: s.shape,
              id: `adminBoundaries.boundaryType.${country}_${s.shape}`
            }),
            title: s.description
          },
          type: 'locations'
        }));
      },
      subtitleProperty: 'name',
      titleProperty: 'description'
    },
    screens: {
      method: async (countryParam, queryParam) => {
        const screensSuggestions = await getScreensInfo(countryParam, queryParam.split(','), excludeScreens, [
          'id',
          'external_deals',
          'device_id'
        ]);
        if (!screensSuggestions || screensSuggestions.items.length === 0) {
          showNotification(
            'map',
            formatMessage({
              defaultMessage: 'No results found',
              id: 'common.noResults'
            })
          );
          return [];
        }
        return screensSuggestions.items.map(s => ({
          ...s,
          suggestion: {
            id: String(s.source_element_id),
            subtitle: s.adExchange_str!,
            title: s.device_id || 'N/A'
          },
          type: 'screens'
        }));
      },
      subtitleProperty: 'name',
      titleProperty: 'description'
    },
    streets: {
      method: async (_, query) => {
        const normalizeCountry = normalizeCountryToCca2(country);
        const streetsSuggestions = await getStreets(query, normalizeCountry);

        if (!streetsSuggestions || !streetsSuggestions.features || streetsSuggestions.features.length === 0) {
          showNotification(
            'map',
            formatMessage({
              defaultMessage: 'No results found',
              id: 'common.noResults'
            })
          );
          return [];
        }

        return streetsSuggestions.features.map(f => {
          const subtitle = f.context
            .slice()
            .reverse()
            .map(item => item.text)
            .join(', ');

          return {
            geometry: f.geometry,
            id: f.id,
            subtitle,
            suggestion: {
              id: f.id,
              subtitle,
              title: f.text
            },
            title: f.text,
            type: 'streets'
          };
        });
      },
      subtitleProperty: 'name',
      titleProperty: 'description'
    }
  };

  const handleSearchInputChange = (newText: string) => {
    setState(prevState => ({
      ...prevState,
      lastSearchedTerm: '',
      search: newText
    }));
  };

  const handleTriggerSearch = (value?: string) => {
    const searchValue = typeof value === 'string' ? value : search;
    setState(prevState => ({
      ...prevState,
      lastSearchedTerm: searchValue,
      search: searchValue
    }));
    isSearchingRef.current = true;
  };

  const handleChangeZoneType = (selectedOption: SelectorOptionPropsInterface) => {
    setState(prevState => ({
      ...prevState,
      suggestionType: selectedOption.value as SuggestionType
    }));
  };

  const { data: suggestionsData, isLoading } = useQueryFetch(
    ['multiFlightMap-suggestions', country, suggestionType, lastSearchedTerm],
    () => suggestionTypeMethodsMap[suggestionType].method(country, lastSearchedTerm),
    {
      enabled: lastSearchedTerm.length > SUGGESTION_COUNT && countries.length > 0
    }
  );

  const dropDownSuggestions: DropdownSuggestion[] =
    suggestionsData?.map((suggestion, index) => {
      return {
        extraData: suggestion,
        id: index.toString(),
        subtitle: suggestion.suggestion.subtitle,
        title: suggestion.suggestion.title,
        ...(suggestion.type === 'screens' &&
          suggestion.publisher_str && {
            tagText: suggestion.publisher_str
          })
      };
    }) ?? [];

  const handleSuggestionSelect = (dropDownSuggestion: DropdownSuggestion) => {
    setState(prevState => ({
      ...prevState,
      lastSearchedTerm: '',
      search: ''
    }));
    const suggestion = dropDownSuggestion.extraData as Suggestions[number];
    if (suggestion.type === 'locations') {
      addTemporaryCartographicLocation(country, suggestion);
    }
    if (suggestion.type === 'streets') {
      onTemporaryPositionChange(suggestion.geometry.coordinates[1], suggestion.geometry.coordinates[0]);
    }
    if (suggestion.type === 'screens') {
      const { suggestion: _, ...screenData } = suggestion;
      setTemporaryScreen(screenData, true);
    }
  };

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        if (search.length > 2 && search !== lastSearchedTerm) {
          handleTriggerSearch();
        } else if (lastSearchedTerm && search === lastSearchedTerm && suggestionsData?.length) {
          handleSuggestionSelect(dropDownSuggestions[0]);
        }
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [search, lastSearchedTerm, suggestionsData, dropDownSuggestions]);

  const currentOptions = type === 'dooh' ? ['screens', 'streets'] : ['locations', 'streets'];

  const options: SelectorOptionPropsInterface[] =
    currentOptions.map((option: string) => ({
      id: option,
      label: formatMessage({
        defaultMessage: option,
        id: `multiflightMapTopBar.search.types.${option}.text`
      }),
      value: option
    })) || [];

  const placeholder = {
    locations: formatMessage({
      defaultMessage: 'Ex: Sevilla',
      id: 'multiflightMapTopBar.search.types.locations.placeholder'
    }),
    screens: formatMessage({
      defaultMessage: 'Ex: Deal or Device ID',
      id: 'multiflightMapTopBar.search.types.screens.placeholder'
    }),
    streets: formatMessage({
      defaultMessage: 'Ex: Paseo La Castellana',
      id: 'multiflightMapTopBar.search.types.streets.placeholder'
    })
  };

  const countText =
    type === 'dooh'
      ? formatMessage({
          defaultMessage: 'Screens',
          id: 'multiflightMapTopBar.screens'
        })
      : formatMessage({
          defaultMessage: 'Activation Areas',
          id: 'multiflightMapTopBar.locations'
        });

  const countTypeNumber =
    type === 'dooh' ? temporaryScreens.filter(f => !f.deleted).length : fullShapes.filter(f => !f.deleted).length;

  return (
    <TopBarStyled className="topbar">
      {volumetryAndForecast}
      <section className="topbar__container">
        <div className="topbar__info">
          <div className="topbar__count">
            <div className="topbar__countNumber">
              {isListLoading ? <LoadingSkeleton count={1} height={18} width={9} /> : countTypeNumber}
            </div>
            <div className="topbar__countText">{countText}</div>
          </div>
          <div className="topbar__infoSeparator" />
          <div className="topbar__count">
            <div className="topbar__countNumber">
              {isListLoading ? (
                <LoadingSkeleton count={1} height={18} width={9} />
              ) : (
                temporaryVenues.filter(v => !v.deleted).length || 0
              )}
            </div>
            <div className="topbar__countText">
              {formatMessage({
                defaultMessage: 'POIs',
                id: 'multiflightMapTopBar.venues'
              })}
            </div>
          </div>
        </div>
        <div className="topbar__search">
          <div className="topbar__searchSelectorDropdown">
            <Selector
              onChange={handleChangeZoneType}
              options={options}
              tiny
              type="simple"
              value={options.filter(option => option.id === suggestionType)}
            />
            <SearchDropdown
              classNames={{
                searchInput: 'topbar__searchDropdown'
              }}
              isLoading={isLoading}
              lens
              limit={5}
              onSearch={handleTriggerSearch}
              onSuggestionSelect={handleSuggestionSelect}
              onTextChange={handleSearchInputChange}
              placeholder={placeholder[suggestionType]}
              prefixId="multiFlightMap"
              suggestions={dropDownSuggestions ?? []}
            />
          </div>
        </div>
      </section>
    </TopBarStyled>
  );
}
